import { GetServerSidePropsContext, NextPage } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Layout, withLayoutProps } from 'slices/Layout'

import { Button } from '@/components/Button/Button'
import { CheckoutCartWrapper } from '@/components/CheckoutCart/CheckoutCartWrapper'
import { Icon } from '@/components/Icon/Icon'
import { BaseSeo } from '@/components/seo/BaseSeo'
import { GOOGLE_EVENTS, trackEvent } from '@/shared/analytics'
import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import { CartLine, useCart } from '@/shared/cart/CartContext'
import { CartLineRow } from '@/shared/cart/components/CartLineRow'
import { CartLoginPanel } from '@/shared/cart/components/CartLoginPanel'
import { TierId, tierToLines, useTierOptions } from '@/shared/cart/tierOptions'
import { formatUsd } from '@/shared/libs/money'
import { pxToRem } from '@/shared/styles/themes'
import { faLockKeyhole } from '@fortawesome/pro-light-svg-icons/faLockKeyhole'
import { faShieldCheck } from '@fortawesome/pro-light-svg-icons/faShieldCheck'
import { faShoppingCart } from '@fortawesome/pro-light-svg-icons/faShoppingCart'
import { Alert, Box, Chip, CircularProgress, Container, Divider, styled, Typography } from '@mui/material'

import type { CartQuoteLine } from '@/shared/api/models'

export const getServerSideProps = async (context: GetServerSidePropsContext) =>
  withLayoutProps(context, { props: {} })

const isUnlockLine = (line: CartLine) =>
  (line.kind === 'software' || line.kind === 'unlimited') && !!line.story_id

// The total the shopper saw before the magic-link round-trip. Anonymous
// quotes assume the first-order discount applies; once signed in the total
// can change, and we must not send them to Stripe at a price they didn't see.
// localStorage (not sessionStorage): magic links usually open in a new tab.
const PRE_LOGIN_TOTAL_KEY = 'pixie_cart_prelogin_total_v1'
const PRE_LOGIN_TOTAL_TTL_MS = 24 * 60 * 60 * 1000

const savePreLoginTotal = (total: string | undefined) => {
  try {
    if (total) window.localStorage.setItem(PRE_LOGIN_TOTAL_KEY, JSON.stringify({ total, savedAt: Date.now() }))
    else window.localStorage.removeItem(PRE_LOGIN_TOTAL_KEY)
  } catch {
    // Storage unavailable — resume will just stop on the cart.
  }
}

const takePreLoginTotal = (): string | null => {
  try {
    const raw = window.localStorage.getItem(PRE_LOGIN_TOTAL_KEY)

    window.localStorage.removeItem(PRE_LOGIN_TOTAL_KEY)

    if (!raw) return null

    const { total, savedAt } = JSON.parse(raw) as { total?: string; savedAt?: number }

    return total && savedAt && Date.now() - savedAt < PRE_LOGIN_TOTAL_TTL_MS ? total : null
  } catch {
    return null
  }
}

const CartPage: NextPage = ({ ...layoutProps }) => {
  const cart = useCart()
  const router = useRouter()
  const { isAuthenticated } = usePixieWorldAuthContext()
  const { tiers, catalog } = useTierOptions()
  const [showLogin, setShowLogin] = useState(false)
  const [resumeNotice, setResumeNotice] = useState<string | null>(null)

  useEffect(() => {
    trackEvent(GOOGLE_EVENTS.VIEW_CART)
  }, [])

  // `?login=1` comes from checkout()/buyNow() while logged out.
  useEffect(() => {
    if (router.query.login === '1' && !isAuthenticated) setShowLogin(true)
  }, [router.query.login, isAuthenticated])

  // `?checkout=1` is where the magic link lands: once the fresh (logged-in)
  // quote is in, continue straight to Stripe — but only if the total is the
  // one the shopper saw before signing in. Anything unexpected (quote error,
  // changed total, unknown pre-login total) stops on the cart instead.
  const hasResumed = useRef(false)

  useEffect(() => {
    if (router.query.checkout !== '1' || hasResumed.current || !cart.isReady) return

    const disarm = () => {
      hasResumed.current = true
      router.replace('/cart', undefined, { shallow: true })
    }

    // Nothing left to buy (e.g. a different account signed in and the cart was cleared).
    if (cart.lines.length === 0) {
      disarm()

      return
    }

    if (cart.quoteError) {
      disarm()
      setResumeNotice('You\'re signed in, but we couldn\'t confirm your total. Retry below, then continue to checkout.')

      return
    }

    if (!isAuthenticated || cart.isQuoteLoading || !cart.quote) return

    disarm()

    const seenTotal = takePreLoginTotal()
    const freshTotal = cart.quote.total

    if (!cart.quote.is_valid) return

    if (seenTotal === null) {
      setResumeNotice('You\'re signed in. Review your order, then continue to checkout.')

      return
    }

    if (parseFloat(seenTotal) !== parseFloat(freshTotal)) {
      setResumeNotice(
        `Your total changed from ${formatUsd(seenTotal)} to ${formatUsd(freshTotal)} now that you're signed in `
        + '(for example, the first-order discount only applies to accounts without a previous book order). '
        + 'Please review your order before checking out.',
      )

      return
    }

    cart.checkout()
  }, [router, isAuthenticated, cart])

  const quoteByKey = useMemo(() => {
    const map = new Map<string, CartQuoteLine>()

    cart.quote?.lines.forEach((line) => {
      if (line.key) map.set(line.key, line)
    })

    return map
  }, [cart.quote])

  // Plan switcher: shown when the cart unlocks a story (software/unlimited).
  // Only book units tagged as coming from the bundle count as its hardcover —
  // a book the shopper added separately is never touched by a plan switch.
  const unlockLine = cart.lines.find(isUnlockLine)
  const hasBundledBook = cart.lines.some((line) => (line.bundleQuantity ?? 0) > 0)

  let currentTier: TierId | null = null

  if (unlockLine?.kind === 'software') currentTier = 'single_story'
  else if (unlockLine?.kind === 'unlimited') currentTier = hasBundledBook ? 'unlimited_book' : 'unlimited'

  const handleSwitchTier = (tier: TierId) => {
    if (!unlockLine?.story_id || tier === currentTier) return

    // Drop the bundle's own book first (removing the Unlimited line untags it),
    // then the current plan, then add the new tier's lines.
    if (currentTier === 'unlimited_book') cart.removeBundledBook()

    cart.removeItem(unlockLine.key)
    tierToLines(tier, unlockLine.story_id, catalog).forEach((line) => cart.addItem(line))
  }

  const layout = (children: React.ReactNode) => (
    <Layout {...layoutProps} noCategoryBrowser slotProps={{ footer: { infoBarOnly: true } }}>
      <BaseSeo title="Cart" />
      {children}
    </Layout>
  )

  if (!cart.isReady) {
    return layout(
      <CenteredContainer>
        <CircularProgress />
      </CenteredContainer>,
    )
  }

  if (cart.lines.length === 0) {
    return layout(
      <CenteredContainer>
        <Icon icon={faShoppingCart} sx={{ fontSize: pxToRem(48), color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
          Your cart is empty
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link href="/shop" passHref legacyBehavior>
            <Button primary>Browse the Shop</Button>
          </Link>
          {isAuthenticated && (
            <Link href="/library" passHref legacyBehavior>
              <Button outline>My Library</Button>
            </Link>
          )}
        </Box>
      </CenteredContainer>,
    )
  }

  const { quote } = cart
  const discount = quote ? parseFloat(quote.discount) : 0
  const hasInvalidLines = !!quote?.lines.some((line) => line.error)
  const canCheckout = !!quote?.is_valid && !cart.isQuoteLoading && !cart.isCheckingOut

  return layout(
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Your Cart
      </Typography>

      <CheckoutCartWrapper>
        {cart.lines.map((line) => (
          <CartLineRow
            key={line.key}
            line={line}
            quoteLine={quoteByKey.get(line.key)}
            isPricing={cart.isQuoteLoading}
            onRemove={() => cart.removeItem(line.key)}
            onQuantityChange={(quantity) => cart.setQuantity(line.key, quantity)}
          />
        ))}
      </CheckoutCartWrapper>

      {currentTier && (
        <PlanSwitcherWrapper>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Your Plan
          </Typography>
          {tiers.filter((tier) => tier.isAvailable).map((tier) => {
            const isSelected = currentTier === tier.id

            return (
              <PlanTierRow
                key={tier.id}
                selected={isSelected}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => handleSwitchTier(tier.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    handleSwitchTier(tier.id)
                  }
                }}
              >
                <PlanTierRadio selected={isSelected} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {tier.name}
                    </Typography>
                    {tier.badge && <PlanBadge>{tier.badge}</PlanBadge>}
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
                    <Typography variant="body2" fontWeight={700}>
                      {tier.price}
                    </Typography>
                    {tier.savingsPercent && <PlanSavingsBadge>{tier.savingsPercent}</PlanSavingsBadge>}
                  </Box>
                  {tier.stickerPrice && (
                    <Typography variant="caption" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
                      {tier.stickerPrice}
                    </Typography>
                  )}
                </Box>
              </PlanTierRow>
            )
          })}
        </PlanSwitcherWrapper>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Order Summary — every figure comes from the server quote */}
      <Box sx={{ mb: 3 }}>
        <SummaryRow>
          <Typography variant="body2" color="text.secondary">
            Subtotal ({cart.itemCount} {cart.itemCount === 1 ? 'item' : 'items'})
          </Typography>
          <SummaryValue value={quote?.subtotal} loading={!quote && !cart.quoteError} />
        </SummaryRow>

        {discount > 0 && (
          <SummaryRow sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography variant="body2" color="success.main">
                Discount
              </Typography>
              {quote?.discount_label && (
                <Chip label={quote.discount_label} size="small" color="success" variant="outlined" />
              )}
            </Box>
            <Typography variant="body2" color="success.main">
              -{formatUsd(quote?.discount)}
            </Typography>
          </SummaryRow>
        )}

        {quote?.requires_shipping && (
          <SummaryRow sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Shipping
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
              {quote.shipping_note || 'Calculated at checkout'}
            </Typography>
          </SummaryRow>
        )}

        <Divider sx={{ my: 1.5 }} />

        <SummaryRow>
          <Typography variant="body1" fontWeight={600}>
            {quote?.requires_shipping ? 'Total before shipping' : 'Total'}
          </Typography>
          <Typography variant="body1" fontWeight={600} component="div">
            <SummaryValue value={quote?.total} loading={!quote && !cart.quoteError} />
          </Typography>
        </SummaryRow>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5, textAlign: 'right' }}>
          All prices in USD
        </Typography>
      </Box>

      {resumeNotice && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setResumeNotice(null)}>
          {resumeNotice}
        </Alert>
      )}

      {cart.quoteError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button text size="small" onClick={cart.retryQuote} disabled={cart.isQuoteLoading}>
              {cart.isQuoteLoading ? 'Retrying…' : 'Retry'}
            </Button>
          )}
        >
          {cart.quoteError}
        </Alert>
      )}

      {hasInvalidLines && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some items in your cart can&apos;t be purchased. Remove them to continue.
        </Alert>
      )}

      {cart.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cart.error}
        </Alert>
      )}

      {!isAuthenticated && showLogin ? (
        <CartLoginPanel onLinkSent={() => savePreLoginTotal(quote?.total)} />
      ) : (
        <Button
          primary
          onClick={() => (isAuthenticated ? cart.checkout() : setShowLogin(true))}
          disabled={isAuthenticated ? !canCheckout : false}
          sx={{ width: '100%' }}
        >
          {cart.isCheckingOut && (
            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
          )}
          {cart.isCheckingOut
            ? 'Taking you to secure checkout…'
            : isAuthenticated ? 'Proceed to Checkout' : 'Log in to Check Out'}
        </Button>
      )}

      <SecureNote>
        <Icon icon={faLockKeyhole} sx={{ fontSize: pxToRem(12) }} />
        <Typography variant="caption" color="text.secondary">
          Payments are processed securely by Stripe.
        </Typography>
      </SecureNote>

      <GuaranteeBadge>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Icon icon={faShieldCheck} sx={{ fontSize: pxToRem(20), color: '#1976d2' }} />
          <Typography variant="body2" fontWeight={600}>
            30-Day Money-Back Guarantee
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Digital purchases: full refund within 30 days. Physical books: 7-day damage
          guarantee.{' '}
          <Link href="/help/refund-policy">
            Learn more
          </Link>
        </Typography>
      </GuaranteeBadge>
    </Container>,
  )
}

const SummaryValue: React.FC<{ value: string | undefined; loading: boolean }> = ({ value, loading }) => {
  if (loading) return <CircularProgress size={14} />

  return (
    <Typography variant="inherit" component="span">
      {value ? formatUsd(value) : '—'}
    </Typography>
  )
}

const CenteredContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
`

const GuaranteeBadge = styled(Box)`
  margin-top: ${pxToRem(12)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  background-color: #e8f4ff;
  border-radius: ${pxToRem(8)};
  text-align: center;
`

const SecureNote = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(6)};
  margin-top: ${pxToRem(10)};
  color: ${props => props.theme.palette.text.secondary};
`

const SummaryRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(12)};
`

export default CartPage

const PlanSwitcherWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: pxToRem(16),
  padding: `${pxToRem(16)} ${pxToRem(16)}`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: pxToRem(12),
}))

const PlanTierRow = styled(Box)<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(10),
  padding: `${pxToRem(10)} ${pxToRem(12)}`,
  borderRadius: pxToRem(8),
  cursor: 'pointer',
  border: `1.5px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
  background: selected ? `${theme.palette.primary.main}08` : 'transparent',
  transition: 'border-color 0.15s, background 0.15s',

  '&:hover': {
    background: selected ? `${theme.palette.primary.main}08` : theme.palette.action.hover,
  },
}))

const PlanTierRadio = styled(Box)<{ selected: boolean }>(({ theme, selected }) => ({
  width: pxToRem(18),
  height: pxToRem(18),
  borderRadius: '50%',
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.text.disabled}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,

  '&::after': {
    content: '""',
    width: pxToRem(9),
    height: pxToRem(9),
    borderRadius: '50%',
    background: selected ? theme.palette.primary.main : 'transparent',
  },
}))

const PlanBadge = styled('span')(({ theme }) => ({
  fontSize: pxToRem(10),
  fontWeight: 700,
  padding: `${pxToRem(1)} ${pxToRem(6)}`,
  borderRadius: pxToRem(8),
  whiteSpace: 'nowrap' as const,
  background: theme.palette.primary.main,
  color: '#fff',
}))

const PlanSavingsBadge = styled('span')(({ theme }) => ({
  fontSize: pxToRem(10),
  fontWeight: 700,
  padding: `${pxToRem(1)} ${pxToRem(6)}`,
  borderRadius: pxToRem(8),
  whiteSpace: 'nowrap' as const,
  background: theme.palette.secondary.main,
  color: '#fff',
}))
